import { useEffect, useRef, useState } from "react";
import {
  Copy,
  ExternalLink,
  KeyRound,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  SegmentedControl,
  Select,
} from "../../design-system";
import { AiModelSelect } from "./llm-model-select";
import type { AiModels } from "./llm-data";
import "./refine.css";
export type AiExecutionView = {
  runner?: {
    id: string;
    name: string;
    status: "online" | "offline";
    version: string | null;
    lastSeenAt: string;
    running: number;
    concurrency: number;
  } | null;
  provider: "api" | "codex";
  installed: boolean;
  version: string | null;
  loggedIn: boolean;
  checking?: boolean;
  limited?: boolean;
  account?: { email: string; plan: string };
  checkedAt: string;
  models: AiModels;
  model: string;
  effort: string;
  pendingLogin?: boolean;
  loginResult?: "expired" | "cancelled";
  canManage: boolean;
  api: { keys: number; defaultModel: string };
  error?: string;
};
export type DeviceLogin = { url: string; code: string; expiresAt: string };
export type AiExecutionActions = {
  save?: (
    patch: Partial<Pick<AiExecutionView, "provider" | "model" | "effort">>,
  ) => Promise<void>;
  refresh?: () => Promise<void>;
  login?: () => Promise<DeviceLogin>;
  cancelLogin?: () => Promise<void>;
  logout?: () => Promise<void>;
  openKeys?: () => void;
};
export type AiExecutionExample =
  | "offline"
  | "undeployed"
  | "api"
  | "plan"
  | "signed-out"
  | "login"
  | "expired"
  | "cancelled"
  | "pending"
  | "missing"
  | "checking"
  | "limited"
  | "readonly"
  | "error";
export function AiExecutionSettings({
  view,
  actions = {},
  initialLogin,
}: {
  view: AiExecutionView;
  actions?: AiExecutionActions;
  initialLogin?: DeviceLogin;
}) {
  const [session, setSession] = useState(initialLogin),
    [logout, setLogout] = useState(false),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [copied, setCopied] = useState(""),
    [now, setNow] = useState(Date.now);
  const pending = useRef(false);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const remaining = session
    ? Math.max(0, Math.ceil((Date.parse(session.expiresAt) - now) / 1000))
    : 0;
  const locked = !view.canManage || busy;
  const safeUrl =
    session && /^https:\/\//i.test(session.url) ? session.url : undefined;
  async function run(fn?: () => Promise<void>) {
    if (pending.current || !fn) return;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  async function login() {
    await run(async () => {
      if (!actions.login) throw Error("로그인이 연결되지 않았습니다.");
      setSession(await actions.login());
    });
  }
  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied("복사했습니다.");
    } catch {
      setCopied("복사하지 못했습니다. 값을 선택해서 복사해 주세요.");
    }
  }
  const status = view.checking
    ? "확인 중"
    : !view.installed
      ? "미설치"
      : view.limited
        ? "한도 초과"
        : view.loggedIn
          ? "로그인됨"
          : "로그인 안 됨";
  return (
    <div className="mrf-page mra-execution" data-ai-execution>
      <section className="mrf-panel">
        <header>
          <div>
            <h3>정형화 AI 실행 방식</h3>
            <p className="mrf-muted">새로 시작하는 정형화에 적용됩니다.</p>
          </div>
          <Badge>{view.canManage ? "관리자 설정" : "읽기 전용"}</Badge>
        </header>
        <fieldset className="mra-choice" disabled={locked || !actions.save}>
          <SegmentedControl
            label="AI 실행 방식"
            value={view.provider}
            options={[
              { value: "api", label: "API 키 · 사용량 과금" },
              { value: "codex", label: "GPT 플랜 · ChatGPT 구독" },
            ]}
            onChange={(v) =>
              void run(() => actions.save!({ provider: v as "api" | "codex" }))
            }
          />
        </fieldset>
      </section>
      {view.runner !== undefined && (
        <section className="mrf-panel" data-ai-runner>
          <header>
            <h3>AI 실행기</h3>
            <Badge tone={view.runner?.status === "online" ? "teal" : "amber"}>
              {view.runner
                ? view.runner.status === "online"
                  ? "연결됨"
                  : "끊김"
                : "배포 안 됨"}
            </Badge>
          </header>
          {view.runner ? (
            <>
              <strong>{view.runner.name}</strong>
              <dl className="mra-facts">
                <div>
                  <dt>Codex 버전</dt>
                  <dd>{view.runner.version ?? "미설치"}</dd>
                </div>
                <div>
                  <dt>마지막 신호</dt>
                  <dd>{view.runner.lastSeenAt || "—"}</dd>
                </div>
                <div>
                  <dt>돌리는 중 / 동시 실행</dt>
                  <dd>
                    {view.runner.running} / {view.runner.concurrency}
                  </dd>
                </div>
              </dl>
              {view.runner.status === "offline" && (
                <p className="mrf-warning" role="status">
                  실행기 연결이 끊겼습니다. 큐는 기다리는 중이며, 연결되면
                  이어서 처리합니다.
                </p>
              )}
            </>
          ) : (
            <>
              <p className="mrf-warning">
                실행기가 배포되지 않았습니다. 정형화를 시작하려면 AI 실행기가
                필요합니다.
              </p>
              <details>
                <summary>배포 안내</summary>
                <p>
                  운영 담당자에게 AI 실행기 배포를 요청해 주세요. 실행기가
                  연결된 뒤 이 화면에서 GPT 로그인과 모델을 설정합니다.
                </p>
              </details>
            </>
          )}
        </section>
      )}
      {view.provider === "api" ? (
        <section className="mrf-panel mra-card">
          <KeyRound size={28} />
          <div>
            <h3>API 키</h3>
            <p>
              사용 중 키 <strong>{view.api.keys}개</strong> · 기본 모델{" "}
              <strong>{view.api.defaultModel || "미설정"}</strong>
            </p>
            <p className="mrf-muted">호출한 토큰에 따라 비용을 기록합니다.</p>
          </div>
          <Button
            variant="secondary"
            onClick={actions.openKeys}
            disabled={!actions.openKeys}
          >
            AI 키 관리 <ExternalLink size={14} />
          </Button>
        </section>
      ) : (
        <section className="mrf-panel">
          <header>
            <div className="mrf-inline">
              <Sparkles size={24} />
              <h3>GPT 플랜</h3>
              <Badge
                tone={view.loggedIn ? "teal" : view.limited ? "coral" : "amber"}
              >
                {status}
              </Badge>
            </div>
            <Button
              variant="ghost"
              onClick={() => void run(actions.refresh)}
              disabled={busy || !actions.refresh}
            >
              <RefreshCw size={15} />
              다시 확인
            </Button>
          </header>
          <dl className="mra-facts">
            <div>
              <dt>설치 버전</dt>
              <dd>{view.version ?? "설치되지 않음"}</dd>
            </div>
            <div>
              <dt>계정</dt>
              <dd>{view.account?.email ?? "계정 정보 제공 안 됨"}</dd>
            </div>
            <div>
              <dt>플랜</dt>
              <dd>{view.account?.plan ?? "—"}</dd>
            </div>
            <div>
              <dt>마지막 확인</dt>
              <dd>{view.checkedAt || "아직 확인하지 않음"}</dd>
            </div>
          </dl>
          {!view.installed ? (
            <div className="mrf-warning">
              AI 실행기에 Codex가 설치되지 않았습니다. 운영 담당자에게 실행기
              설치 확인을 요청해 주세요.
            </div>
          ) : view.pendingLogin && !session ? (
            <p className="mrf-warning" role="status">
              다른 관리자가 로그인을 진행 중입니다. 승인 결과를 기다리고
              있습니다.
            </p>
          ) : view.loggedIn ? (
            <>
              <AiModelSelect
                models={{ ...view.models, billing: "plan" }}
                value={view.model}
                onChange={(model) => void run(() => actions.save!({ model }))}
                disabled={locked || !actions.save}
                alwaysShow
              />
              <Select
                label="추론 강도"
                value={view.effort}
                disabled={locked || !actions.save}
                onChange={(e) =>
                  void run(() => actions.save!({ effort: e.target.value }))
                }
              >
                {["", "minimal", "low", "medium", "high", "xhigh"].map((v) => (
                  <option key={v} value={v}>
                    {v || "없음 · Codex 기본"}
                  </option>
                ))}
              </Select>
              <Button
                variant="secondary"
                onClick={() => setLogout(true)}
                disabled={locked || !actions.logout}
              >
                로그아웃
              </Button>
            </>
          ) : (
            <Button
              onClick={() => void login()}
              disabled={
                locked ||
                view.checking ||
                !actions.login ||
                view.runner === null ||
                view.runner?.status === "offline"
              }
            >
              GPT 로그인
            </Button>
          )}
          {view.loginResult && (
            <p role="status">
              {view.loginResult === "expired"
                ? "로그인 코드가 만료되었습니다. 새 로그인을 시작해 주세요."
                : "로그인을 취소했습니다."}
            </p>
          )}
          <p className="mrf-warning">
            플랜 한도는 이 계정에 걸립니다. 한도에 닿으면 정형화가 멈추고 API
            키로 바꿀 수 있습니다.
          </p>
          <p className="mrf-muted">
            플랜은 정액 구독입니다. 사용량에는 토큰을 기록하고 호출 비용은
            $0으로 표시합니다.
          </p>
        </section>
      )}
      {(error || view.error) && (
        <p role="alert" className="mrf-error">
          {error || view.error}
        </p>
      )}
      {busy && <p role="status">변경 사항을 처리하고 있습니다…</p>}
      {session && view.canManage && !view.loggedIn && (
        <Dialog
          open
          title="GPT 로그인"
          onOpenChange={(v) =>
            !v &&
            !busy &&
            void run(async () => {
              if (!actions.cancelLogin)
                throw Error("로그인 취소가 연결되지 않았습니다.");
              await actions.cancelLogin();
              setSession(undefined);
            })
          }
          footer={
            <DialogActions
              secondary={{
                label: "로그인 취소",
                onClick: () =>
                  void run(async () => {
                    if (!actions.cancelLogin)
                      throw Error("로그인 취소가 연결되지 않았습니다.");
                    await actions.cancelLogin();
                    setSession(undefined);
                  }),
                disabled: busy,
              }}
              primary={{
                label: remaining ? "승인 상태 확인" : "새 코드 받기",
                onClick: () =>
                  void (remaining ? run(actions.refresh) : login()),
                disabled: busy || !view.canManage,
              }}
            />
          }
        >
          <div className="mrf-form">
            {remaining > 0 ? (
              <>
                <p>아래 주소를 열고 코드를 입력하세요.</p>
                <div className="mra-url">
                  <code>{session.url}</code>
                  <Button
                    variant="ghost"
                    onClick={() => void copy(session.url)}
                  >
                    <Copy size={15} />
                    주소 복사
                  </Button>
                  {safeUrl && (
                    <a href={safeUrl} target="_blank" rel="noopener noreferrer">
                      주소 열기 <ExternalLink size={14} />
                    </a>
                  )}
                </div>
                <div className="mra-code">
                  <strong>{session.code}</strong>
                  <Button
                    variant="secondary"
                    onClick={() => void copy(session.code)}
                  >
                    코드 복사
                  </Button>
                </div>
                <p className="mra-countdown">
                  남은 시간 {Math.floor(remaining / 60)}:
                  {String(remaining % 60).padStart(2, "0")}
                </p>
                <p role="status" className="mrf-inline">
                  <RefreshCw size={16} />
                  승인 기다리는 중…
                </p>
              </>
            ) : (
              <p role="alert">
                로그인 코드가 만료되었습니다. 새 코드를 받아 주세요.
              </p>
            )}
            {copied && <p role="status">{copied}</p>}
            {error && (
              <p role="alert" className="mrf-error">
                {error}
              </p>
            )}
          </div>
        </Dialog>
      )}
      {logout && (
        <Dialog
          open
          title="GPT 플랜 로그아웃"
          onOpenChange={(v) => !v && !busy && setLogout(false)}
          footer={
            <DialogActions
              secondary={{
                label: "취소",
                onClick: () => setLogout(false),
                disabled: busy,
              }}
              primary={{
                label: busy ? "로그아웃 중…" : "로그아웃",
                onClick: () =>
                  void run(async () => {
                    if (!actions.logout)
                      throw Error("로그아웃이 연결되지 않았습니다.");
                    await actions.logout();
                    setLogout(false);
                    setSession(undefined);
                  }),
                disabled: busy,
              }}
            />
          }
        >
          <p>다음 정형화부터 플랜을 사용할 수 없습니다. 로그아웃할까요?</p>
          {error && <p role="alert">{error}</p>}
        </Dialog>
      )}
    </div>
  );
}
export function AiExecutionPreview({
  example = "plan",
  onOpenKeys,
  canManage = true,
}: {
  example?: AiExecutionExample;
  onOpenKeys?: () => void;
  canManage?: boolean;
}) {
  const [view, setView] = useState<AiExecutionView>({
    runner:
      example === "undeployed"
        ? null
        : {
            id: "runner-demo",
            name: "운영 AI 실행기",
            status: example === "offline" ? "offline" : "online",
            version: example === "missing" ? null : "codex 0.162.1",
            lastSeenAt: "2026-10-22 11:32",
            running: example === "offline" ? 0 : 1,
            concurrency: 2,
          },
    provider: example === "api" ? "api" : "codex",
    installed: example !== "missing" && example !== "undeployed",
    version: example === "missing" ? null : "codex 0.162.1",
    loggedIn: ["plan", "readonly", "limited"].includes(example),
    checking: example === "checking",
    limited: example === "limited",
    checkedAt: "2026-10-22 11:32",
    models: {
      default: "plan-model",
      allowed: ["plan-model", "plan-small"],
      billing: "plan",
    },
    model: "plan-model",
    effort: "medium",
    pendingLogin: example === "pending",
    loginResult: example === "cancelled" ? "cancelled" : undefined,
    canManage: canManage && example !== "readonly",
    api: { keys: 2, defaultModel: "gpt-5-mini" },
    error:
      example === "error" ? "실행 방식 상태를 불러오지 못했습니다." : undefined,
  });
  const [grant] = useState<DeviceLogin | undefined>(() =>
    ["login", "expired"].includes(example)
      ? {
          url: "https://auth.openai.com/codex/device",
          code: "DEMO-4821",
          expiresAt: new Date(
            Date.now() + (example === "expired" ? -1000 : 900000),
          ).toISOString(),
        }
      : undefined,
  );
  const owns = useRef(!!grant);
  return (
    <AiExecutionSettings
      view={view}
      initialLogin={grant}
      actions={{
        openKeys: onOpenKeys,
        save: async (patch) => setView((v) => ({ ...v, ...patch })),
        refresh: async () =>
          setView((v) => ({
            ...v,
            checking: false,
            error: undefined,
            loggedIn: owns.current || v.loggedIn,
            pendingLogin: false,
          })),
        login: async () => {
          owns.current = true;
          return {
            url: "https://auth.openai.com/codex/device",
            code: "DEMO-4821",
            expiresAt: new Date(Date.now() + 900000).toISOString(),
          };
        },
        cancelLogin: async () => {
          owns.current = false;
          setView((v) => ({
            ...v,
            loginResult: "cancelled",
            pendingLogin: false,
          }));
        },
        logout: async () => {
          owns.current = false;
          setView((v) => ({ ...v, loggedIn: false, loginResult: undefined }));
        },
      }}
    />
  );
}
